import type jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  PDFQuoteData,
  buildPalette,
  tintColor,
  shadeColor,
  loadLogo,
  getJsPdfImageFormat,
  resolveDocumentTitle,
  renderDocumentSubtitle,
  fitHeaderTitle,
  formatQuoteDate,
  calcValidityDate,
  buildItemRows,
  formatCurrency,
  renderDiagnosisSection,
  renderServiceOrderSections,
  renderPaymentSection,
  renderExtraFields,
  renderPixSection,
  renderPhotosSection,
  renderSignatureSection,
  renderFooter,
  detectDocumentKind,
  formatDocument,
  pdfQuoteTotals,
  renderTotalsBreakdown,
  renderLogoWatermark,
} from './shared';

/**
 * "Onda" template — cabeçalho com duas ondas sobrepostas (bezier em vetor) em
 * tons da cor da marca, título grande e dados do cliente em estilo formulário
 * (rótulo + valor sobre linha fina). Tabela com grade na cor da marca.
 */
export async function render(doc: jsPDF, data: PDFQuoteData): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  // Marca-d'agua da logo: PRIMEIRA instrucao de desenho da pagina.
  // jsPDF pinta na ordem das chamadas e nao tem z-index, entao desenhada
  // depois ela cobriria a tabela e o total em vez de ficar atras deles.
  await renderLogoWatermark(doc, data);
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 16;
  const contentWidth = pageWidth - margin * 2;
  const { primaryColor, grayVeryLight, grayLight, grayText, darkText, white } =
    buildPalette(data.brandColorRGB);

  const dark = shadeColor(primaryColor, 0.45);
  const light = tintColor(primaryColor, 0.65);
  const pale = tintColor(primaryColor, 0.9);

  const logoBase64 = await loadLogo(data.company?.logoUrl);

  // === 1. ONDAS DO CABEÇALHO (vetor puro, sem imagem) ===
  drawHeaderWaves(doc, pageWidth, light, dark);

  // Logo num cartão branco sobre as ondas, no canto superior esquerdo.
  if (logoBase64) {
    const logoBox = 22;
    doc.setFillColor(...white);
    doc.roundedRect(margin, 5, logoBox, logoBox, 2, 2, 'F');
    try {
      const format = getJsPdfImageFormat(logoBase64);
      doc.addImage(logoBase64, format, margin + 2, 7, logoBox - 4, logoBox - 4);
    } catch {
      /* cartão branco fica como moldura vazia */
    }
  }

  // === 2. TÍTULO (esq.) + DADOS DA EMPRESA (dir.) ===
  let yPos = 44;
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...dark);
  // Os dados da empresa ocupam a metade DIREITA na mesma altura (companyY = 40
  // contra o baseline 44 daqui), e eles já se limitam a meia página.
  const tituloCabecalho = fitHeaderTitle(
    doc,
    resolveDocumentTitle(data),
    data.company?.name ? pageWidth / 2 - margin : contentWidth,
    24,
  );
  doc.setFontSize(tituloCabecalho.size);
  doc.text(tituloCabecalho.text, margin, yPos);
  // Subtitulo: 0 de avanco quando nao ha (o PDF do historico nao muda).
  yPos += renderDocumentSubtitle(doc, data, {
    x: margin, y: yPos,
    maxWidth: data.company?.name ? pageWidth / 2 - margin : contentWidth, color: grayText,
  });

  let companyY = 40;
  if (data.company?.name) {
    doc.setFontSize(10);
    doc.setTextColor(...darkText);
    const nameLine = doc.splitTextToSize(data.company.name, pageWidth / 2 - margin)[0];
    doc.text(nameLine, pageWidth - margin, companyY, { align: 'right' });
    companyY += 4.2;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...grayText);
    const companyLines: string[] = [];
    if (data.company.cnpj) companyLines.push(`CNPJ ${data.company.cnpj}`);
    if (data.company.address) {
      companyLines.push(doc.splitTextToSize(data.company.address, pageWidth / 2 - margin)[0]);
    }
    const contact: string[] = [];
    if (data.company.phone) contact.push(data.company.phone);
    if (data.company.email) contact.push(data.company.email);
    if (contact.length) companyLines.push(contact.join('  |  '));
    for (const line of companyLines) {
      doc.text(line, pageWidth - margin, companyY, { align: 'right' });
      companyY += 3.6;
    }
  }

  yPos = Math.max(yPos + 8, companyY + 6);

  // === 3. DADOS DO CLIENTE (estilo formulário: rótulo + valor sobre linha) ===
  const formRows: Array<[string, string]> = [
    ['Cliente:', data.client.name || 'Não informado'],
  ];
  if (data.client.document) {
    formRows.push([
      detectDocumentKind(data.client.document) === 'cnpj' ? 'CNPJ:' : 'CPF:',
      formatDocument(data.client.document),
    ]);
  }
  if (data.client.address) formRows.push(['Endereço:', data.client.address]);
  if (data.client.phone) formRows.push(['Telefone:', data.client.phone]);
  formRows.push(['Data de emissão:', formatQuoteDate(data.quoteDate)]);
  if (data.validityDays && data.quoteDate) {
    formRows.push(['Válido até:', calcValidityDate(data.quoteDate, data.validityDays)]);
  }
  if (data.reference) formRows.push(['Referência:', data.reference]);

  const labelWidth = 30;
  doc.setFontSize(9);
  for (const [label, value] of formRows) {
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...dark);
    doc.text(label, margin, yPos);

    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...darkText);
    const valueLine = doc.splitTextToSize(value, contentWidth - labelWidth - 2)[0];
    doc.text(valueLine, margin + labelWidth, yPos);

    doc.setDrawColor(...light);
    doc.setLineWidth(0.4);
    doc.line(margin + labelWidth - 2, yPos + 1.4, pageWidth - margin, yPos + 1.4);
    yPos += 6.4;
  }
  yPos += 4;

  // === 4. TABELA DE ITENS (grade na cor da marca, cabeçalho branco) ===
  const { hasAnyDiscount, headers, body } = buildItemRows(data.items);
  const columnStyles = hasAnyDiscount
    ? {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 15, halign: 'center' as const },
        2: { cellWidth: 13, halign: 'center' as const },
        3: { cellWidth: 27, halign: 'right' as const },
        4: { cellWidth: 20, halign: 'center' as const },
        5: { cellWidth: 29, halign: 'right' as const },
      }
    : {
        0: { cellWidth: 'auto' as const, halign: 'left' as const },
        1: { cellWidth: 18, halign: 'center' as const },
        2: { cellWidth: 13, halign: 'center' as const },
        3: { cellWidth: 30, halign: 'right' as const },
        4: { cellWidth: 30, halign: 'right' as const },
      };

  autoTable(doc, {
    startY: yPos,
    head: headers,
    body,
    theme: 'grid',
    headStyles: {
      fillColor: white,
      textColor: dark,
      fontStyle: 'bold',
      fontSize: 9,
      halign: 'center',
      cellPadding: 3.5,
      lineColor: primaryColor,
      lineWidth: 0.4,
    },
    bodyStyles: {
      fontSize: 8.5,
      cellPadding: 3,
      lineColor: primaryColor,
      lineWidth: 0.3,
    },
    alternateRowStyles: { fillColor: pale },
    columnStyles,
    margin: { left: margin, right: margin },
  });

  const tableEndY = (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
  yPos = tableEndY + 5;

  // === 5. TOTAL ===
  const totals = pdfQuoteTotals(data);
  const totalBoxWidth = 76;
  yPos = renderTotalsBreakdown(doc, totals, yPos, {
    margin,
    pageWidth,
    grayText,
    width: totalBoxWidth,
  });

  const totalBoxX = pageWidth - margin - totalBoxWidth;
  doc.setFillColor(...dark);
  doc.roundedRect(totalBoxX, yPos, totalBoxWidth, 14, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...white);
  doc.text('TOTAL:', totalBoxX + 5, yPos + 9);
  doc.setFontSize(13);
  doc.text(formatCurrency(totals.total), pageWidth - margin - 5, yPos + 9, { align: 'right' });
  yPos += 14 + 7;

  // === 6. OBSERVAÇÕES ===
  if (data.warranty || data.notes || (data.validityDays && data.quoteDate)) {
    const obsLines: string[] = [];
    if (data.validityDays && data.quoteDate) {
      obsLines.push(
        `Validade: este documento é válido até ${calcValidityDate(data.quoteDate, data.validityDays)}.`,
      );
    }
    if (data.warranty) {
      obsLines.push(...(doc.splitTextToSize(`Garantia: ${data.warranty}`, contentWidth - 8) as string[]));
    }
    if (data.notes) {
      obsLines.push(...(doc.splitTextToSize(data.notes, contentWidth - 8) as string[]));
    }
    const obsHeight = obsLines.length * 4 + 12;
    if (yPos + obsHeight > pageHeight - 30) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFillColor(...pale);
    doc.roundedRect(margin, yPos, contentWidth, obsHeight, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...dark);
    doc.text('Observações', margin + 4, yPos + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(...darkText);
    doc.text(obsLines, margin + 4, yPos + 12);
    yPos += obsHeight + 6;
  }

  // === 7. DIAGNÓSTICO / PAGAMENTO / PIX (compartilhadas; o retorno avança o
  // yPos — jsPDF não tem layout, armadilha #13) ===
  yPos = renderServiceOrderSections(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = renderDiagnosisSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  // Campos extras do prestador: 0 de avanço quando não há (#L5).
  yPos = renderExtraFields(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });
  yPos = renderPaymentSection(doc, data, yPos, {
    margin,
    pageWidth,
    pageHeight,
    grayText,
    darkText,
  });

  yPos = await renderPixSection(doc, data, yPos, {
    primaryColor,
    grayText,
    darkText,
    margin,
    pageWidth,
    pageHeight,
  });

  // === 8. TERMOS ===
  if (data.termsConditions) {
    const lines = doc.splitTextToSize(data.termsConditions, contentWidth - 8);
    const termsHeight = Math.max(20, lines.length * 3.8 + 12);
    if (yPos + termsHeight > pageHeight - 30) {
      doc.addPage();
      yPos = 20;
    }
    doc.setFillColor(...grayVeryLight);
    doc.roundedRect(margin, yPos, contentWidth, termsHeight, 2, 2, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...grayText);
    doc.text('Termos e condições', margin + 4, yPos + 6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.text(lines, margin + 4, yPos + 11);
    yPos += termsHeight + 7;
  }

  // === 9. FOTOS ===
  yPos = await renderPhotosSection(doc, data, yPos, {
    darkText,
    grayText,
    grayLight,
    margin,
    pageWidth,
    pageHeight,
  });

  // === 10. ASSINATURAS ===
  await renderSignatureSection(doc, data, {
    darkText,
    grayText,
    margin,
    pageWidth,
    pageHeight,
    contentBottom: yPos,
  });

  // === 11. RODAPÉ ===
  renderFooter(doc, data, {
    primaryColor,
    grayText,
    margin,
    pageWidth,
    pageHeight,
  });
}

/**
 * Duas ondas sobrepostas no topo: uma faixa clara mais alta e uma fita escura
 * por cima. Beziers com coordenadas RELATIVAS (contrato do `doc.lines`): cada
 * sêxtupla é [c1x, c1y, c2x, c2y, fimX, fimY] a partir do ponto corrente.
 */
function drawHeaderWaves(
  doc: jsPDF,
  pageWidth: number,
  light: [number, number, number],
  dark: [number, number, number],
): void {
  const w = pageWidth;

  // Faixa clara: retângulo do topo com a borda inferior ondulada.
  doc.setFillColor(...light);
  doc.lines(
    [
      [w, 0],
      [0, 26],
      [-w * 0.3, 12, -w * 0.7, -16, -w, -4],
    ],
    0, 0, [1, 1], 'F', true,
  );

  // Fita escura cruzando por cima.
  doc.setFillColor(...dark);
  doc.lines(
    [
      [w, 0],
      [0, 6],
      [-w * 0.35, 18, -w * 0.65, -14, -w, 8],
    ],
    0, 0, [1, 1], 'F', true,
  );
}
